import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { useUtmTaxonomyStore, type Zone } from '@/store/utm-taxonomy-store';
import UtmDataTable from '@/components/utm/shared/UtmDataTable';
import UtmZoneDetailDrawer from './UtmZoneDetailDrawer';

const UtmZoneList = () => {
  const {
    zones,
    groups,
    fetchZones,
    fetchTaxonomy,
    deleteZone,
    publishZone,
    unpublishZone,
    selectedZone,
    selectZone,
    isLoading,
  } = useUtmTaxonomyStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);

  useEffect(() => {
    fetchTaxonomy();
    fetchZones();
  }, [fetchTaxonomy, fetchZones]);

  const filteredZones = useMemo(() => {
    return zones.filter((zone) => {
      return (
        zone.zoneName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zone.zoneCode.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [zones, searchQuery]);

  const handleDeleteConfirm = async () => {
    if (!zoneToDelete) return;
    try {
      await deleteZone(zoneToDelete.id);
      setZoneToDelete(null);
    } catch (error) {
      console.error('Failed to delete zone:', error);
    }
  };

  const handleTogglePublish = async (zone: Zone) => {
    try {
      if (zone.status === 'PUBLISHED') {
        await unpublishZone(zone.id);
      } else {
        await publishZone(zone.id);
      }
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  const getGroupName = (groupId: string) => {
    return groups.find((g) => g.id === groupId)?.name || 'Unknown';
  };

  const columns = [
    {
      key: 'zoneCode',
      label: 'Zone Code',
      render: (zone: Zone) => (
        <span className="font-mono text-primary">{zone.zoneCode}</span>
      ),
    },
    {
      key: 'zoneName',
      label: 'Zone Name',
    },
    {
      key: 'groupId',
      label: 'Group',
      render: (zone: Zone) => getGroupName(zone.groupId),
    },
    {
      key: 'status',
      label: 'Status',
      render: (zone: Zone) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            zone.status === 'PUBLISHED'
              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
              : 'bg-muted text-muted-foreground border border-border'
          }`}
        >
          {zone.status}
        </span>
      ),
    },
    {
      key: 'geometry',
      label: 'Has Geometry',
      render: (zone: Zone) => (
        <span className={zone.geometry ? 'text-green-400' : 'text-muted-foreground'}>
          {zone.geometry ? '✓' : '✗'}
        </span>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Zones</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage UTM airspace zones and their properties
          </p>
        </div>
        <Link
          to="/admin/uzamaps/zones/new"
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
        >
          <Plus size={18} />
          <span>Create Zone</span>
        </Link>
      </div>

      {/* Data Table */}
      <UtmDataTable
        data={filteredZones}
        columns={columns}
        searchPlaceholder="Search zones by name or code..."
        onSearch={setSearchQuery}
        itemsPerPage={15}
        mobileCardRender={(zone) => (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-sm font-semibold text-primary">{zone.zoneCode}</div>
                <div className="mt-1 font-semibold text-foreground">{zone.zoneName}</div>
                <div className="mt-1 text-xs text-muted-foreground">Group: {getGroupName(zone.groupId)}</div>
              </div>
              <span
                className={`rounded border px-2 py-1 text-[11px] font-medium ${
                  zone.status === 'PUBLISHED'
                    ? 'border-green-500/50 bg-green-500/20 text-green-400'
                    : 'border-border bg-muted text-muted-foreground'
                }`}
              >
                {zone.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Geometry: {zone.geometry ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => selectZone(zone.id)}
                className="rounded p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                title="View Details"
              >
                <Eye size={16} />
              </button>
              <button
                onClick={() => handleTogglePublish(zone)}
                className={`rounded p-2 transition-colors ${
                  zone.status === 'PUBLISHED'
                    ? 'text-green-400 hover:bg-green-500/10 hover:text-green-300'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
                title={zone.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              >
                {zone.status === 'PUBLISHED' ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <Link
                to={`/admin/uzamaps/zones/${zone.id}/edit`}
                className="rounded p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                title="Edit"
              >
                <Edit size={16} />
              </Link>
              <button
                onClick={() => setZoneToDelete(zone)}
                className="rounded p-2 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-destructive"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}
        actions={(zone) => (
          <>
            <button
              onClick={() => selectZone(zone.id)}
              className="p-2 text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded transition-colors"
              title="View Details"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => handleTogglePublish(zone)}
              className={`p-2 rounded transition-colors ${
                zone.status === 'PUBLISHED'
                  ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                  : 'text-muted-foreground hover:text-accent-foreground hover:bg-accent'
              }`}
              title={zone.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
            >
              {zone.status === 'PUBLISHED' ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <Link
              to={`/admin/uzamaps/zones/${zone.id}/edit`}
              className="p-2 text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded transition-colors"
              title="Edit"
            >
              <Edit size={16} />
            </Link>
            <button
              onClick={() => setZoneToDelete(zone)}
              className="p-2 text-muted-foreground hover:text-destructive hover:bg-red-500/10 rounded transition-colors"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      />

      <UtmZoneDetailDrawer zone={selectedZone} isOpen={Boolean(selectedZone)} onClose={() => selectZone(null)} />

      {/* Delete Confirmation Modal */}
      {zoneToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-foreground mb-2">Delete Zone</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete "{zoneToDelete.zoneName}"? This action cannot be
              undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setZoneToDelete(null)}
                className="px-4 py-2 bg-muted hover:bg-accent text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isLoading}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 disabled:opacity-50 text-destructive-foreground rounded-lg transition-colors"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtmZoneList;
